// Dashboard: what is about to be invoiced, and what the pipeline is worth.

import { useState, type ReactNode } from "react";
import type { BillingLineupReport, PipelineReport } from "@crm/shared";
import styles from "./DashboardPage.module.css";
import { UnresolvedCount } from "../components/SplitSource.js";
import { Badge, toneFor } from "../design/Badge.js";
import { Card, CardHeader } from "../design/Card.js";
import { FxDisplayFootnote, FxFootnote } from "../design/FxFootnote.js";
import { QueryBoundary } from "../design/QueryBoundary.js";
import { Segmented, type SegmentedOption } from "../design/Segmented.js";
import { StatTile, StatTileRow } from "../design/StatTile.js";
import { EmptyRow, Table, Td, Th } from "../design/Table.js";
import { usePageChrome } from "../design/pageChrome.js";
import { formatCount, formatCurrencyTotals, formatMoney } from "../format/money.js";
import { formatMonth, formatWeek } from "../format/calendarDate.js";
import { chargeStatusColor, chargeStatusLabel } from "../format/labels.js";
import { useReportCurrency } from "../format/reportCurrency.js";
import { useBillingLineupReport, usePipelineReport } from "../api/queries.js";
import { useDebouncedValue } from "../lib/useDebouncedValue.js";

const WEEK_WINDOWS: ReadonlyArray<SegmentedOption<number>> = [
  { value: 4, label: "4w", title: "Next 4 weeks" },
  { value: 8, label: "8w", title: "Next 8 weeks" },
  { value: 13, label: "13w", title: "Next 13 weeks" },
  { value: 26, label: "26w", title: "Next 26 weeks" },
];

export function DashboardPage(): ReactNode {
  // One currency for both panels, the reader's report currency: the two
  // reports are read together, and a dashboard mixing CAD in one table with USD
  // in the other would invite someone to add the totals up.
  const currency = useReportCurrency();
  const [weeks, setWeeks] = useState<number>(8);

  // Clicking through the windows asks for the last one only.
  const lineup = useBillingLineupReport(currency, useDebouncedValue(weeks));
  const pipeline = usePipelineReport(currency);

  usePageChrome({
    title: "Dashboard",
  });

  return (
    <div className={styles.page}>
      <KpiRow lineup={lineup.data} pipeline={pipeline.data} />

      <div className={styles.panels}>
        <Card>
          <CardHeader
            title="Billing lineup"
            description="Charges falling due in the window, by ISO week and status."
            actions={
              <Segmented
                label="Lineup window"
                variant="inset"
                options={WEEK_WINDOWS}
                value={weeks}
                onChange={setWeeks}
              />
            }
          />
          <QueryBoundary query={lineup} label="the billing lineup">
            {(report) => <BillingLineupPanel report={report} />}
          </QueryBoundary>
        </Card>

        <Card>
          <CardHeader
            title="Weighted pipeline"
            description="Value × win probability, by expected start month."
          />
          <QueryBoundary query={pipeline} label="the pipeline report">
            {(report) => <PipelinePanel report={report} />}
          </QueryBoundary>
        </Card>
      </div>
    </div>
  );
}

/**
 * The four figures above the fold. They read from two independent queries, so
 * each tile resolves on its own — a pending tile shows a dash rather than a
 * zero, because a zero here is a real and very different answer.
 */
function KpiRow({
  lineup,
  pipeline,
}: {
  lineup: BillingLineupReport | undefined;
  pipeline: PipelineReport | undefined;
}): ReactNode {
  // ONE FIGURE PER SOURCE CURRENCY, never one converted number. A week is
  // now a row per currency, so "ready to invoice" is summed per currency too —
  // adding a USD week to a CAD one would state a figure no invoice could carry.
  const readyRows = lineup === undefined ? [] : lineup.rows.filter((row) => row.status === "ready");
  // The report's own totals give the currency order, so the two tiles read the
  // same way round.
  const readyTotals = (lineup?.totals ?? [])
    .filter((total) => readyRows.some((row) => row.currency === total.currency))
    .map((total) => ({
      currency: total.currency,
      minor: readyRows
        .filter((row) => row.currency === total.currency)
        .reduce((sum, row) => sum + row.gross_minor, 0),
    }));
  const readyCount = readyRows.reduce((total, row) => total + row.item_count, 0);

  return (
    <StatTileRow>
      <StatTile
        label="Due in window"
        value={
          lineup === undefined
            ? null
            : formatCurrencyTotals(
                lineup.totals.map((total) => ({
                  currency: total.currency,
                  minor: total.gross_minor,
                })),
              )
        }
        hint={
          lineup === undefined
            ? "Gross, all statuses"
            : `${lineup.params.from_date} → ${lineup.params.to_date}`
        }
      />
      <StatTile
        label="Ready to invoice"
        value={lineup === undefined ? null : formatCurrencyTotals(readyTotals)}
        hint={`${formatCount(readyCount)} item${readyCount === 1 ? "" : "s"} flagged ready`}
      />
      <StatTile
        label="Weighted pipeline"
        value={
          pipeline === undefined
            ? null
            : formatMoney(pipeline.totals.weighted_gross_minor, pipeline.currency)
        }
        hint={
          pipeline === undefined
            ? "Win-adjusted"
            : `${formatMonth(pipeline.params.from_month)} – ${formatMonth(pipeline.params.to_month)}`
        }
      />
      <StatTile
        label="Weighted our share"
        value={
          pipeline === undefined
            ? null
            : formatMoney(pipeline.totals.weighted_our_share_minor, pipeline.currency)
        }
        hint="After the resolved revenue split"
      />
    </StatTileRow>
  );
}

/**
 * The lineup, week by week.
 *
 * "Our share" is summed over the items whose split RESOLVES (migration 0013),
 * while "Gross" covers every item in the bucket — so the two columns can cover
 * different sets of rows, and `unresolved_item_count` is what says by how many.
 * It rides in the our-share cell rather than in a column of its own: it
 * qualifies that one figure, and a column that is zero on nearly every row
 * would cost width to say nothing.
 */
function BillingLineupPanel({ report }: { report: BillingLineupReport }): ReactNode {
  // The code rides on every amount only when there is something to tell apart.
  // One currency is the ordinary case, and repeating "CAD" down a column of a
  // single-currency table costs width to say nothing.
  const omitCurrencyCode = report.totals.length <= 1;
  return (
    <>
      <Table label="Billing lineup by week and status">
        <thead>
          <tr>
            <Th>Week</Th>
            <Th>Status</Th>
            <Th numeric>Items</Th>
            <Th numeric>Gross</Th>
            <Th numeric>Our share</Th>
          </tr>
        </thead>
        <tbody>
          {report.rows.length === 0 ? (
            <EmptyRow
              colSpan={5}
              title="Nothing is due in this window"
              description="Widen the window, or check that the billing schedule has been generated for these deals."
            />
          ) : (
            report.rows.map((row) => (
              // The currency is part of the key: a week is one row per source
              // currency, so two rows can share a week and a status.
              <tr key={`${row.week}:${row.status}:${row.currency}`}>
                <Td mono nowrap>
                  {formatWeek(row.week, row.week_start)}
                </Td>
                <Td>
                  <Badge shape="chip" tone={toneFor(chargeStatusColor(row.status))}>
                    {chargeStatusLabel(row.status)}
                  </Badge>
                </Td>
                <Td numeric mono muted>
                  {formatCount(row.item_count)}
                </Td>
                <Td numeric mono>
                  {formatMoney(row.gross_minor, row.currency, { omitCurrencyCode })}
                </Td>
                <Td numeric mono strong>
                  {formatMoney(row.our_share_minor, row.currency, { omitCurrencyCode })}
                  <UnresolvedCount count={row.unresolved_item_count} of="bills" />
                </Td>
              </tr>
            ))
          )}
        </tbody>
        {report.rows.length > 0 && (
          <tfoot>
            {/* ONE FOOT ROW PER CURRENCY. A single "Total" over two of them
                would be a figure no invoice could carry. */}
            {report.totals.map((total) => (
              <tr key={total.currency}>
                <Td>{report.totals.length === 1 ? "Total" : `Total ${total.currency}`}</Td>
                <Td />
                <Td numeric mono muted>
                  {formatCount(total.item_count)}
                </Td>
                <Td numeric mono>
                  {formatMoney(total.gross_minor, total.currency, { omitCurrencyCode })}
                </Td>
                <Td numeric mono>
                  {formatMoney(total.our_share_minor, total.currency, { omitCurrencyCode })}
                  <UnresolvedCount count={total.unresolved_item_count} of="bills" />
                </Td>
              </tr>
            ))}
          </tfoot>
        )}
      </Table>
      <FxDisplayFootnote fx={report.fx} generatedAt={report.generated_at} />
    </>
  );
}

/**
 * AN EMPTY PIPELINE IS ONE FACT, NOT THIRTEEN ROWS OF IT.
 *
 * The report answers with a row per month in the window whether or not a deal
 * lands in one, so a quarter with nothing in the funnel drew thirteen lines of
 * $0.00 and a $0.00 total under them. Every figure there is true and none is
 * worth a line. `every` over no rows is true, so a report with no rows at all
 * reads the same way.
 */
function hasDeals(report: PipelineReport): boolean {
  return !report.rows.every((row) => row.deal_count === 0);
}

function PipelinePanel({ report }: { report: PipelineReport }): ReactNode {
  const populated = hasDeals(report);

  return (
    <>
      <Table label="Weighted pipeline by month">
        <thead>
          <tr>
            <Th>Start month</Th>
            <Th numeric>Deals</Th>
            <Th numeric>Gross</Th>
            <Th numeric>Weighted</Th>
          </tr>
        </thead>
        <tbody>
          {!populated ? (
            <EmptyRow
              colSpan={4}
              title={`No deals in pipeline for ${formatMonth(report.params.from_month)} – ${formatMonth(report.params.to_month)}`}
              description="No open deal has a start date landing in these months. Deals already won or live are excluded — they are contracted, not forecast."
            />
          ) : (
            report.rows.map((row) => (
              <tr key={row.month}>
                <Td strong nowrap>
                  {formatMonth(row.month)}
                </Td>
                <Td numeric mono muted>
                  {formatCount(row.deal_count)}
                </Td>
                <Td numeric mono muted>
                  {formatMoney(row.unweighted_gross_minor, report.currency, {
                    omitCurrencyCode: true,
                  })}
                </Td>
                <Td numeric mono strong>
                  {formatMoney(row.weighted_gross_minor, report.currency, {
                    omitCurrencyCode: true,
                  })}
                </Td>
              </tr>
            ))
          )}
        </tbody>
        {populated && (
          <tfoot>
            <tr>
              <Td>Total</Td>
              {/* Deliberately not summed: a deal with a fuzzy start date is
                  counted in every month its range touches (D5), so adding the
                  column would report more deals than exist. */}
              <Td numeric>
                <span
                  className={styles.notTotalled}
                  title="Not summed: a deal spread across months is counted in each one, so a total would double-count it."
                >
                  —
                </span>
              </Td>
              <Td numeric mono>
                {formatMoney(report.totals.unweighted_gross_minor, report.currency, {
                  omitCurrencyCode: true,
                })}
              </Td>
              <Td numeric mono>
                {formatMoney(report.totals.weighted_gross_minor, report.currency, {
                  omitCurrencyCode: true,
                })}
              </Td>
            </tr>
          </tfoot>
        )}
      </Table>
      <FxFootnote currency={report.currency} fx={report.fx} generatedAt={report.generated_at} />
    </>
  );
}
